import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CorpusSummary } from "../../../api/content/corporaApi";
import type { DocumentOrder } from "../../../api/content/sourcesApi";
import { useProjects } from "../../../projects/projectContext";
import { formatCollectionVersionRef } from "../../../shared/references/contentReferences";
import IconButton from "../../../ui/buttons/IconButton";
import CopyReference from "../../../ui/content/CopyReference";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { classNames } from "../../../ui/styles/classNames";
import { setKeyForm, useKeyForm } from "../../search";
import { labelOf, useVersionLabels } from "../../versions";
import DocumentOrderChoice from "./DocumentOrderChoice";

interface CorpusCardProps {
  corpus: CorpusSummary;
  /** How many documents its lists keep, under the search's filter. */
  documentCount: number;
  /** The lists' order: one choice for every corpus listed. */
  order: DocumentOrder;
  onOrderChange: (order: DocumentOrder) => void;
}

/**
 * A corpus in the Documents sidebar, compact (F137; P-C, P-D): its name, and behind a small
 * arrow right of it, what is seldom needed while working: its latest version, how many
 * documents its list keeps, the lists' order and, where the corpus declares one, its keys'
 * short form (F136). Folded at first.
 */
export default function CorpusCard({ corpus, documentCount, order, onOrderChange }: CorpusCardProps) {
  const { t } = useTranslation();
  const [unfolded, setUnfolded] = useState(false);
  const detailsId = useId();
  const detailsLabel = t("sources.corpus.details", { corpus: corpus.name });
  return (
    <div className="space-y-1 px-1">
      <div className="flex items-center gap-1">
        <p className="app-title min-w-0 text-sm font-semibold [overflow-wrap:anywhere]">{corpus.name}</p>
        <IconButton
          label={detailsLabel}
          title={detailsLabel}
          aria-expanded={unfolded}
          aria-controls={detailsId}
          className="h-6 w-6 shrink-0 p-1"
          onClick={() => { setUnfolded(!unfolded); }}
        >
          <ChevronDownIcon className={classNames("h-4 w-4 transition-transform", !unfolded && "-rotate-90")} aria-hidden="true" />
        </IconButton>
      </div>
      {unfolded ? (
        <div id={detailsId} className="app-text-muted space-y-1 text-xs">
          <LatestCorpusVersion corpus={corpus} />
          <p>{t("sources.list.titleWithCount", { count: documentCount })}</p>
          <DocumentOrderChoice order={order} onOrderChange={onOrderChange} />
          {corpus.writesShortKeys ? <ShortKeysChoice /> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Keys in their corpus's short form (F136; v1's "Kurz-ID Format"): one choice for every corpus,
 * offered by a corpus that declares a short form.
 */
function ShortKeysChoice() {
  const { t } = useTranslation();
  const keyForm = useKeyForm();
  return (
    <OptionLabel size="small">
      <input type="checkbox" checked={keyForm === "short"} onChange={(event) => { setKeyForm(event.target.checked ? "short" : "full"); }} />
      {t("sources.corpus.shortKeys")}
    </OptionLabel>
  );
}

/**
 * The corpus's latest version by its label, "Latest version 1.2.0" (F77, F92; ADR-0033: a corpus
 * labels itself), never by its ID. Until the label is read, or without one, "Latest version"
 * alone. The exact reference is for citing, behind the copy button.
 */
function LatestCorpusVersion({ corpus }: { corpus: CorpusSummary }) {
  const { t } = useTranslation();
  const projectId = useProjects().selectedProjectId ?? "";
  const reference = formatCollectionVersionRef(corpus.latestVersion);
  const labels = useVersionLabels(projectId, { itemVersions: [], collectionVersions: projectId === "" ? [] : [reference] });
  const label = labelOf(labels, reference);
  return (
    <p className="flex items-center gap-1">
      {label === null ? t("sources.corpus.latest") : t("sources.corpus.latestLabelled", { version: label.version })}
      <CopyReference value={reference} />
    </p>
  );
}
